import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/router';
import { useQueryClient } from '@tanstack/react-query';
import type { CreditTransaction } from '@/components/credit-transactions-list';

// 캐시 관련 상수 정의
const CACHE_KEY_PREFIX: string = 'user_subscription_cache_';
const CACHE_EXPIRY_DAYS: number = 7;

// 캐시 인터페이스 정의
interface UserSubscriptionCache {
  availableCredits: number;
  usedCredits: number;
  totalCredits: number;
  accountQuota: number;
  lastUpdated: number;
  expiryDate: number;
}

// Function to check if user has enough credits (auto-initializes for new users)
export async function checkCredits(amount = 1) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    // 로그인 상태에서 실제 크레딧 확인
    const { data, error } = await supabase
      .from('user_chat_credits')
      .select('available_credits, account_quota') // account_quota도 함께 조회
      .eq('user_id', session.user.id)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking credits:', error);
      return false;
    }
    
    // 크레딧 데이터가 없거나 0인 경우 - 요금제 구독 필요
    if (!data || data.available_credits < amount) {
      console.log('크레딧 부족 - 구독이 필요하거나 크레딧 충전 필요');
      return false;
    }
    
    // 크레딧이 있는 경우
    return true;
  } catch (error) {
    console.error('Error in checkCredits:', error);
    return false; // 오류 발생 시 안전하게 false 반환
  }
}

export function useCredits() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
 
  const [availableCredits, setAvailableCredits] = useState<number | null>(null);
  const [usedCredits, setUsedCredits] = useState<number | null>(null);
  const [totalCredits, setTotalCredits] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialLoadAttempted, setInitialLoadAttempted] = useState(false);
  const [accountQuota, setAccountQuota] = useState<number | null>(null);
  
  // 중복 무효화 방지용
  const lastInvalidationTime = useRef<number>(0);
  
  // loadCreditData 함수 선언을 위한 참조
  const loadCreditDataRef = useRef<(forceRefresh?: boolean) => Promise<void>>();

  // Helper function to initialize credits for a new user
  const initializeUserCredit = useCallback(async () => {
    if (!session) return false;
    
    try {
      console.log('Initializing credits for new user:', session.user.id);
      
      // Try to directly call an RPC function or insert credit record
      const { error } = await supabase.rpc('admin_add_user_credits', {
        p_user_id: session.user.id,
        p_credits_to_add: 0, 
        p_description: '초기 가입 (무료 크레딧 없음)'
      });
      
      if (error) {
        console.error('Failed to call RPC function to initialize credits:', error);
        // Fallback to direct insert
        const { error: insertError } = await supabase
          .from('user_chat_credits')
          .insert({
            user_id: session.user.id,
            available_credits: 0, 
            used_credits: 0,
            total_credits_received: 0,
            account_quota: 2 // 무료 계정은 2개 계정 제한
          })
          .select();
          
        if (insertError) {
          console.error('Failed to insert initial credits directly:', insertError);
        }
        
        // Also record a transaction
        await supabase
          .from('chat_credit_transactions')
          .insert({
            user_id: session.user.id,
            amount: 0, 
            description: '초기 가입 (무료 크레딧 없음)',
            transaction_type: 'add'
          });
          
        return !insertError;
      }
      
      return true;
    } catch (err) {
      console.error('Error in initializeUserCredit:', err);
      return false;
    }
  }, [session]);

  // 캐시 저장 함수
  const saveToCache = useCallback((data: {
    availableCredits: number;
    usedCredits: number;
    totalCredits: number;
    accountQuota: number;
  }): void => {
    if (!session) return;
    
    const cacheKey: string = `${CACHE_KEY_PREFIX}${session.user.id}`;
    const now: number = Date.now();
    const expiryDate: number = now + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    
    const cacheData: UserSubscriptionCache = {
      ...data,
      lastUpdated: now,
      expiryDate
    };
    
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log('구독 정보 캐시 저장 완료, 만료일:', new Date(expiryDate).toLocaleString());
    } catch (err) {
      console.error('캐시 저장 중 오류 발생:', err);
    }
  }, [session]);

  // 캐시 조회 함수
  const getFromCache = useCallback((): UserSubscriptionCache | null => {
    if (!session?.user?.id) return null;
    
    const cacheKey: string = `${CACHE_KEY_PREFIX}${session.user.id}`;
    
    try {
      const cachedData: string | null = localStorage.getItem(cacheKey);
      if (!cachedData) return null;
      
      const parsedData: UserSubscriptionCache = JSON.parse(cachedData);
      const now: number = Date.now();
      
      // 캐시 만료 확인
      if (parsedData.expiryDate < now) {
        console.log('캐시 만료됨, 새로운 데이터 요청 필요');
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      console.log('캐시에서 구독 정보 로드됨, 저장일:', new Date(parsedData.lastUpdated).toLocaleString());
      return parsedData;
    } catch (err) {
      console.error('캐시 조회 중 오류 발생:', err);
      return null;
    }
  }, [session]);

  // 캐시 삭제 함수
  const clearCache = useCallback(() => {
    if (!session) return;
    
    if (!session?.user?.id) return;
    const cacheKey: string = `${CACHE_KEY_PREFIX}${session.user.id}`;
    localStorage.removeItem(cacheKey);
    console.log('구독 정보 캐시 삭제됨');
  }, [session]);

  // 크레딧 데이터 로드 함수
  const loadCreditData = useCallback(async (forceRefresh: boolean = false): Promise<void> => {
    if (!session) return;
    
    setInitialLoadAttempted(true);
    
    // 강제 새로고침이 아니라면 캐시 확인
    if (!forceRefresh) {
      const cachedData: UserSubscriptionCache | null = getFromCache();
      
      // 유효한 캐시가 있으면 사용
      if (cachedData) {
        setAvailableCredits(cachedData.availableCredits);
        setUsedCredits(cachedData.usedCredits);
        setTotalCredits(cachedData.totalCredits);
        setAccountQuota(cachedData.accountQuota);
        setLoading(false);
        return;
      }
    }
    
    // 캐시가 없거나 강제 새로고침인 경우 API 호출
    setLoading(true);
    setError(null);
    
    try {
      console.log('서버에서 구독 및 크레딧 데이터 로딩 중:', session.user.id);
      
      // Fetch user's credit data
      const { data, error } = await supabase
        .from('user_chat_credits')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching credits:', error);
        setError('크레딧 정보를 불러오는데 실패했습니다.');
        
        if (error.code === 'PGRST116' || error.code === '406') {
          console.log('No credits found for user, they might be a new user');
          
          // For new users, initialize with 0 credits
          if (availableCredits === null) {
            const initialResult = await initializeUserCredit();
            if (initialResult) {
              setAvailableCredits(0); 
              setUsedCredits(0);
              setTotalCredits(0);
              setAccountQuota(2); // 기본 쿼터 설정
            } else {
              setAvailableCredits(0);
              setUsedCredits(0);
              setTotalCredits(0);
              setAccountQuota(2);
            }
          }
        }
      } else if (data) {
        console.log('Credit data loaded:', data);
        // 구독 플랜의 크레딧 확인을 위한 로직
        // 크레딧이 0개이거나 구독이 없는 경우 0개 유지
        if (data.available_credits > 0) {
          setAvailableCredits(data.available_credits);
        } else {
          setAvailableCredits(0);
        }
        setUsedCredits(data.used_credits);
        setTotalCredits(data.total_credits_received);
        setAccountQuota(data.account_quota || 2);
        
        // Invalidate accountQuota query to ensure it stays in sync, but only if enough time has passed
        const now = Date.now();
        if (now - lastInvalidationTime.current > 10000) { // 10초 간격으로만 무효화
          queryClient.invalidateQueries({ queryKey: ['accountQuota'] });
          lastInvalidationTime.current = now;
        }
      } else {
        // No credits data yet - first-time user or no records found
        console.log('No credits data found - user might be new');
        const initialResult = await initializeUserCredit();
        if (initialResult) {
          setAvailableCredits(0); 
          setUsedCredits(0);
          setTotalCredits(0);
          setAccountQuota(2);
        } else {
          setAvailableCredits(0);
          setUsedCredits(0);
          setTotalCredits(0);
          setAccountQuota(2);
        }
      }
      
      // Fetch recent transactions
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('chat_credit_transactions')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (transactionsError) {
        console.error('Error fetching transactions:', transactionsError);
      } else {
        console.log('Transactions loaded:', transactionsData?.length || 0);
        setTransactions(transactionsData || []);
      }
    } catch (err) {
      console.error('Unexpected error in loadCreditData:', err);
      setError('크레딧 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [session, getFromCache, saveToCache, queryClient, availableCredits, initializeUserCredit]);

  useEffect(() => {
    if (!session) {
      setLoading(false);
      return;
    }
    
    loadCreditData();
    
    // 리스너는 정리를 위한 ID를 저장
    const userId = session.user.id;
    const creditChangesChannel = 'user_chat_credits_channel_' + userId.substring(0, 8);
    const transactionChangesChannel = 'credit_transactions_channel_' + userId.substring(0, 8);
    
    // Subscribe to credit changes with explicit table filters and debouncing
    let debounceTimerId: ReturnType<typeof setTimeout>;
    
    const creditChanges = supabase
      .channel(creditChangesChannel)
      .on('postgres_changes', {
        event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
        schema: 'public',
        table: 'user_chat_credits',
        filter: `user_id=eq.${session.user.id}`
      }, () => {
        // Debounce to prevent multiple rapid updates
        clearTimeout(debounceTimerId);
        debounceTimerId = setTimeout(() => {
          console.log('구독/크레딧 정보 변경 감지, 캐시 업데이트 중...');
          clearCache(); // 캐시 초기화
          loadCreditData(true); // 강제 새로고침으로 데이터 로드
        }, 2000); // 2초 지연
      })
      .subscribe();
      
    // Also subscribe to transaction changes
    const transactionChanges = supabase
      .channel(transactionChangesChannel)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_credit_transactions',
        filter: `user_id=eq.${session.user.id}`
      }, () => {
        // 별도 타이머 사용
        clearTimeout(debounceTimerId);
        debounceTimerId = setTimeout(() => {
          console.log('New credit transaction detected, reloading...');
          loadCreditData();
        }, 2000); // 2초 지연
      })
      .subscribe();
      
    return () => {
      clearTimeout(debounceTimerId);
      creditChanges.unsubscribe();
      transactionChanges.unsubscribe();
    };
  }, [session, loadCreditData, clearCache]);

  // loadCreditData 함수 할당
  useEffect(() => {
    loadCreditDataRef.current = loadCreditData;
  }, [loadCreditData]);
  
  // 초기 로드 및 캐시 처리
  useEffect(() => {
    if (!initialLoadAttempted && session && availableCredits === null) {
      // 먼저 캐시를 확인하고, 없으면 서버에서 로드
      const cachedData = getFromCache();
      
      if (cachedData) {
        // 캐시된 데이터가 있으면 사용
        setAvailableCredits(cachedData.availableCredits);
        setUsedCredits(cachedData.usedCredits);
        setTotalCredits(cachedData.totalCredits);
        setAccountQuota(cachedData.accountQuota);
        setLoading(false);
        setInitialLoadAttempted(true);
      } else {
        // 캐시된 데이터가 없으면 서버에서 로드
        const retryTimer = setTimeout(() => {
          console.log('초기 구독/크레딧 정보 로드 시도');
          loadCreditData();
        }, 2000);
        
        return () => clearTimeout(retryTimer);
      }
    }
  }, [initialLoadAttempted, availableCredits, session, loadCreditData, getFromCache, setLoading, setInitialLoadAttempted]);

  async function useCredit(_amount = 1, _description = '대화 메시지') {
    if (!session) return { success: false, message: '로그인이 필요합니다.' };
    
    try {
      console.log(`Using ${_amount} credit(s) for: ${_description}`);
      
      const { data, error } = await supabase.rpc('use_chat_credits', {
        p_user_id: session.user.id,
        p_credits_to_use: _amount,
        p_description: _description
      });
      
      if (error) {
        console.error('Error using credits:', error);
        return { success: false, message: error.message };
      }
      
      console.log('Credit usage result:', data);
      
      // Update local state optimistically
      setAvailableCredits(prev => prev !== null ? prev - _amount : null);
      setUsedCredits(prev => prev !== null ? prev + _amount : null);
      
      // Reload full credit data
      setTimeout(() => {
        clearCache(); // 캐시 초기화 후
        loadCreditData(true); // 강제 새로고침으로 데이터 로드
      }, 2000);
      
      return { success: true, message: 'Credits used successfully' };
    } catch (error) {
      console.error('Unexpected error in useCredit:', error);
      return { success: false, message: 'An unexpected error occurred' };
    }
  }

  return {
    availableCredits,
    usedCredits,
    totalCredits,
    accountQuota,
    transactions,
    loading,
    error,
    useCredit,
    refreshCredits: () => loadCreditDataRef.current?.(true), // 강제 새로고침 함수
    clearSubscriptionCache: clearCache // 캐시 초기화 함수 노출
  };
}